import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Send, Loader2, BookOpen, Sparkles, Bot, User,
  CheckCircle, FileText, AlertTriangle, Clock, ThumbsUp, ThumbsDown, ArrowUpRight,
  BarChart3, Link2, Ticket, Shield, TrendingUp, UserCheck, Play, XCircle, Hash
} from 'lucide-react';
import { format } from 'date-fns';
import { useRestartOnboarding } from '@/components/OnboardingWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: { color: '#FF9500', label: 'Open' },
  assigned: { color: '#007AFF', label: 'Assigned' },
  in_progress: { color: '#AF52DE', label: 'In Progress' },
  resolved: { color: '#34C759', label: 'Resolved' },
  closed: { color: '#9CA3AF', label: 'Closed' },
};

const SLA_COLORS = {
  on_track: { bg: '#34C759', label: 'On Track' },
  at_risk: { bg: '#FF9500', label: 'At Risk' },
  breached: { bg: '#FF3B30', label: 'Breached' },
  met: { bg: '#34C759', label: 'Met' },
};

const SlaBadge = ({ status }) => {
  const s = SLA_COLORS[status] || SLA_COLORS.on_track;
  return <Badge className="text-[7px] px-1 py-0" style={{ backgroundColor: s.bg + '18', color: s.bg }} data-testid={`sla-badge-${status}`}>{s.label}</Badge>;
};

const HelpCenterPage = () => {
  const { token, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [report, setReport] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [ticketForm, setTicketForm] = useState({ title: '', description: '', category: 'general', priority: 'medium' });
  const [resolveForm, setResolveForm] = useState({ resolution_notes: '', linked_articles: [] });
  const [statusFilter, setStatusFilter] = useState('');
  const [articles, setArticles] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTickets(); fetchArticles(); }, [statusFilter]);

  const fetchTickets = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/helpdesk/tickets`, { params, headers });
      setTickets(res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchArticles = async () => {
    try { const res = await axios.get(`${API}/articles`, { headers }); setArticles(res.data); } catch {}
  };

  const fetchReport = async () => {
    try {
      const [rpt, gp] = await Promise.all([
        axios.get(`${API}/helpdesk/report`, { headers }),
        axios.get(`${API}/helpdesk/knowledge-gaps`, { headers }),
      ]);
      setReport(rpt.data);
      setGaps(gp.data);
    } catch {}
  };

  const handleCreate = async () => {
    if (!ticketForm.title.trim()) { toast.error('Title required'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/helpdesk/tickets`, ticketForm, { headers });
      setSuggestions(res.data._suggested_articles_full || []);
      setSelectedTicket(res.data);
      if (res.data._suggested_articles_full?.length > 0) {
        toast.success(`Ticket TKT-${String(res.data.ticket_number).padStart(4, '0')} created — ${res.data._suggested_articles_full.length} article(s) may help!`);
      } else {
        toast.success(`Ticket TKT-${String(res.data.ticket_number).padStart(4, '0')} created — assigned to support queue`);
        setIsCreateOpen(false);
      }
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const handleSelfResolve = async (articleId) => {
    if (!selectedTicket) return;
    try {
      await axios.post(`${API}/helpdesk/tickets/${selectedTicket.ticket_id}/self-resolve`, { article_id: articleId }, { headers });
      toast.success('Great! Ticket resolved via self-service');
      setIsCreateOpen(false); setSuggestions([]); setSelectedTicket(null);
      setTicketForm({ title: '', description: '', category: 'general', priority: 'medium' });
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDismissSuggestions = () => {
    setSuggestions([]);
    setIsCreateOpen(false);
    setTicketForm({ title: '', description: '', category: 'general', priority: 'medium' });
    toast.info('Ticket submitted to support queue');
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/helpdesk/tickets/${ticketId}`, { headers });
      setSelectedTicket(res.data);
      setIsViewOpen(true);
    } catch { toast.error('Failed to load ticket'); }
  };

  const handleAgentResolve = async () => {
    if (!selectedTicket) return;
    try {
      await axios.post(`${API}/helpdesk/tickets/${selectedTicket.ticket_id}/resolve`, resolveForm, { headers });
      toast.success('Ticket resolved');
      setIsResolveOpen(false); setIsViewOpen(false);
      setResolveForm({ resolution_notes: '', linked_articles: [] });
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleHoldTicket = async () => {
    if (!selectedTicket) return;
    try {
      const res = await axios.post(`${API}/helpdesk/tickets/${selectedTicket.ticket_id}/hold`, {}, { headers });
      toast.success('You are now working on this ticket');
      setSelectedTicket({ ...selectedTicket, ...res.data });
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAssignToMe = async () => {
    if (!selectedTicket) return;
    try {
      const res = await axios.post(`${API}/helpdesk/tickets/${selectedTicket.ticket_id}/assign`, {}, { headers });
      toast.success('Ticket assigned to you');
      setSelectedTicket({ ...selectedTicket, ...res.data });
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    try {
      await axios.post(`${API}/helpdesk/tickets/${selectedTicket.ticket_id}/close`, {}, { headers });
      toast.success('Ticket closed');
      setIsViewOpen(false);
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDraftArticle = async (ticketId) => {
    try {
      const res = await axios.post(`${API}/helpdesk/tickets/${ticketId}/draft-article`, {}, { headers });
      toast.success('Draft KB article created — awaiting approval');
      if (selectedTicket?.ticket_id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, knowledge_draft_created: true, knowledge_article_id: res.data.article_id }));
      }
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleFeedback = async (articleId, helpful) => {
    try {
      await axios.post(`${API}/helpdesk/articles/${articleId}/feedback`, { helpful }, { headers });
      toast.success(helpful ? 'Glad it helped!' : 'Thanks for the feedback — flagged for review');
    } catch {}
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatQuestion.trim() || chatLoading) return;
    const q = chatQuestion.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: q }]);
    setChatQuestion('');
    setChatLoading(true);
    try {
      const history = chatMessages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      const res = await axios.post(`${API}/docs/ask`, { question: q, history }, { headers });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not process that. Try browsing the docs directly.' }]);
    } finally { setChatLoading(false); }
  };

  useEffect(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [chatMessages]);

  const getStatus = (s) => STATUS_COLORS[s] || STATUS_COLORS.open;
  const fmtTktNum = (n) => n ? `TKT-${String(n).padStart(4, '0')}` : '';
  const worstSla = (tkt) => {
    const r = tkt.sla_response_status || 'on_track';
    const s = tkt.sla_resolution_status || 'on_track';
    if (r === 'breached' || s === 'breached') return 'breached';
    if (r === 'at_risk' || s === 'at_risk') return 'at_risk';
    if (r === 'met' || s === 'met') return 'met';
    return 'on_track';
  };

  return (
    <div data-testid="help-center-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Help Center & Service Desk</h1>
          <p className="text-[#4B5563] text-sm">Submit tickets, get AI-powered article suggestions, and track resolutions</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setIsCreateOpen(true); setSuggestions([]); setSelectedTicket(null); setTicketForm({ title: '', description: '', category: 'general', priority: 'medium' }); }} data-testid="create-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />New Ticket
        </Button>
      </div>

      <Tabs defaultValue="tickets" onValueChange={v => { if (v === 'report') fetchReport(); }}>
        <TabsList className="w-full justify-start mb-4 flex-wrap">
          <TabsTrigger value="tickets" data-testid="tab-tickets"><Ticket className="w-3.5 h-3.5 mr-1" />Tickets</TabsTrigger>
          <TabsTrigger value="kb" data-testid="tab-kb"><BookOpen className="w-3.5 h-3.5 mr-1" />Knowledge Base</TabsTrigger>
          <TabsTrigger value="ask" data-testid="tab-ask"><Sparkles className="w-3.5 h-3.5 mr-1" />Ask AI</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report"><BarChart3 className="w-3.5 h-3.5 mr-1" />Impact Report</TabsTrigger>
        </TabsList>

        {/* ==================== TICKETS TAB ==================== */}
        <TabsContent value="tickets">
          <div className="flex gap-2 mb-4 flex-wrap">
            {['', 'open', 'assigned', 'in_progress', 'resolved', 'closed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-[#002FA7] text-white' : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]'}`}
                data-testid={`filter-${s || 'all'}`}>
                {s ? getStatus(s).label : 'All'}
              </button>
            ))}
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div> : (
            <div className="space-y-2" data-testid="tickets-list">
              {tickets.length === 0 ? (
                <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF]">
                  <Ticket className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
                  <p className="text-sm">No tickets found. Click "New Ticket" to submit a request.</p>
                </CardContent></Card>
              ) : tickets.map(tkt => {
                const st = getStatus(tkt.status);
                const slaStatus = worstSla(tkt);
                return (
                  <Card key={tkt.ticket_id} className="border-[#E5E7EB] hover:border-[#002FA7]/20 transition-colors cursor-pointer" onClick={() => handleViewTicket(tkt.ticket_id)} data-testid={`ticket-${tkt.ticket_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-[9px] font-mono font-bold text-[#002FA7] flex-shrink-0" data-testid={`ticket-number-${tkt.ticket_id}`}>{fmtTktNum(tkt.ticket_number)}</span>
                          <Badge className="text-[8px] px-1.5 py-0 flex-shrink-0" style={{ backgroundColor: st.color + '15', color: st.color }}>{st.label}</Badge>
                          {tkt.status !== 'closed' && tkt.status !== 'resolved' && <SlaBadge status={slaStatus} />}
                          {tkt.status === 'resolved' || tkt.status === 'closed' ? <SlaBadge status={slaStatus === 'breached' ? 'breached' : 'met'} /> : null}
                          <h4 className="text-sm font-medium text-[#0A0A0A] truncate">{tkt.title}</h4>
                          <Badge variant="outline" className="text-[8px] px-1 border-[#E5E7EB] flex-shrink-0">{tkt.category}</Badge>
                          {tkt.escalated && <Badge className="text-[7px] bg-[#FF3B30]/10 text-[#FF3B30] px-1 flex-shrink-0">Escalated</Badge>}
                          {tkt.resolution_type === 'self_service' && <Badge className="text-[8px] bg-[#34C759]/10 text-[#34C759] px-1 flex-shrink-0">Self-service</Badge>}
                          {tkt.knowledge_draft_created && <Badge className="text-[8px] bg-[#5856D6]/10 text-[#5856D6] px-1 flex-shrink-0">KB Draft</Badge>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {tkt.assigned_to_name && <span className="text-[9px] text-[#007AFF]" title="Assigned to"><UserCheck className="w-2.5 h-2.5 inline mr-0.5" />{tkt.assigned_to_name}</span>}
                          <span className="text-[9px] text-[#9CA3AF]">{tkt.submitted_by_name}</span>
                          <span className="text-[9px] text-[#9CA3AF]">{format(new Date(tkt.created_at), 'MMM d')}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ==================== KNOWLEDGE BASE TAB ==================== */}
        <TabsContent value="kb">
          <div className="space-y-2" data-testid="kb-list">
            {articles.filter(a => a.is_published).slice(0, 30).map(art => (
              <Card key={art.article_id} className="border-[#E5E7EB]" data-testid={`kb-article-${art.article_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-[#0A0A0A] mb-0.5">{art.title}</h4>
                      <p className="text-xs text-[#4B5563] line-clamp-2">{art.content?.substring(0, 150)}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {art.tags?.slice(0, 3).map((t, i) => <Badge key={i} variant="outline" className="text-[8px] px-1 border-[#E5E7EB]">{t}</Badge>)}
                        <span className="text-[9px] text-[#9CA3AF]">{art.views || 0} views</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                      <button onClick={() => handleFeedback(art.article_id, true)} className="p-1.5 rounded hover:bg-[#34C759]/10 transition-colors" data-testid={`helpful-${art.article_id}`} title="Helpful">
                        <ThumbsUp className="w-3.5 h-3.5 text-[#34C759]" />
                      </button>
                      <button onClick={() => handleFeedback(art.article_id, false)} className="p-1.5 rounded hover:bg-[#FF3B30]/10 transition-colors" data-testid={`not-helpful-${art.article_id}`} title="Not helpful">
                        <ThumbsDown className="w-3.5 h-3.5 text-[#FF3B30]" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ==================== ASK AI TAB ==================== */}
        <TabsContent value="ask">
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-0 flex flex-col h-[500px]">
              <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <Sparkles className="w-10 h-10 text-[#002FA7] mb-3" />
                    <h3 className="text-base font-semibold mb-1">Ask KStreak AI</h3>
                    <p className="text-xs text-[#9CA3AF] max-w-sm mb-4">Ask questions about the platform, features, or troubleshooting.</p>
                    <div className="grid grid-cols-2 gap-2 max-w-md">
                      {['How do I export tasks?', 'How does Kanban board work?', 'What is knowledge degradation?', 'How to create a webinar?'].map((s, i) => (
                        <button key={i} onClick={() => setChatQuestion(s)} className="text-xs px-3 py-2 border border-[#E5E7EB] rounded-lg hover:border-[#002FA7] hover:bg-[#F0F4FF] text-[#4B5563]" data-testid={`chat-suggestion-${i}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                ) : chatMessages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
                    {msg.role === 'assistant' && <div className="w-6 h-6 rounded-lg bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0"><Bot className="w-3 h-3 text-[#002FA7]" /></div>}
                    <div className={`max-w-[75%] px-3 py-2 rounded-xl text-xs ${msg.role === 'user' ? 'bg-[#002FA7] text-white' : 'bg-[#F3F4F6] text-[#0A0A0A]'}`}>{msg.content}</div>
                    {msg.role === 'user' && <div className="w-6 h-6 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0"><User className="w-3 h-3" /></div>}
                  </div>
                ))}
                {chatLoading && <div className="flex gap-2"><div className="w-6 h-6 rounded-lg bg-[#002FA7]/10 flex items-center justify-center"><Bot className="w-3 h-3 text-[#002FA7]" /></div><div className="px-3 py-2 rounded-xl bg-[#F3F4F6]"><Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" /></div></div>}
              </div>
              <form onSubmit={handleChat} className="p-3 border-t border-[#E5E7EB] flex gap-2">
                <Input placeholder="Ask a question..." value={chatQuestion} onChange={e => setChatQuestion(e.target.value)} className="text-xs" data-testid="chat-input" />
                <Button type="submit" size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" disabled={chatLoading} data-testid="chat-send"><Send className="w-3.5 h-3.5" /></Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== IMPACT REPORT TAB ==================== */}
        <TabsContent value="report" className="space-y-4">
          {report ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="report-summary">
                {[
                  { label: 'Total Tickets', value: report.summary.total_tickets, icon: Ticket, color: '#002FA7' },
                  { label: 'Self-Service Rate', value: `${report.summary.self_service_rate}%`, icon: CheckCircle, color: report.summary.self_service_rate >= 30 ? '#34C759' : '#FF9500' },
                  { label: 'Agent+Article Rate', value: `${report.summary.agent_article_rate}%`, icon: Link2, color: report.summary.agent_article_rate >= 50 ? '#34C759' : '#FF9500' },
                  { label: 'New KB Articles', value: report.summary.new_articles_from_tickets, icon: FileText, color: '#5856D6' },
                  { label: 'Avg Resolution (hrs)', value: report.summary.avg_resolution_hours, icon: Clock, color: report.summary.avg_resolution_hours <= 24 ? '#34C759' : '#FF9500' },
                  { label: 'Escalated', value: report.summary.escalated_count, icon: TrendingUp, color: report.summary.escalated_count > 0 ? '#FF3B30' : '#34C759' },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <Card key={i} className="border-[#E5E7EB]" data-testid={`report-stat-${i}`}>
                      <CardContent className="p-3 text-center">
                        <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                        <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                        <p className="text-[9px] text-[#9CA3AF]">{s.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* SLA Compliance */}
              <Card className="border-[#E5E7EB]" data-testid="sla-compliance-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-[#002FA7]" />SLA Compliance</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 rounded-lg bg-[#F3F4F6]" data-testid="sla-response-compliance">
                      <p className="text-2xl font-bold" style={{ color: report.sla.response_compliance >= 80 ? '#34C759' : report.sla.response_compliance >= 60 ? '#FF9500' : '#FF3B30' }}>{report.sla.response_compliance}%</p>
                      <p className="text-[9px] text-[#9CA3AF]">Response SLA</p>
                      <p className="text-[8px] text-[#9CA3AF] mt-0.5">{report.sla.response_breached} breached</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-[#F3F4F6]" data-testid="sla-resolution-compliance">
                      <p className="text-2xl font-bold" style={{ color: report.sla.resolution_compliance >= 80 ? '#34C759' : report.sla.resolution_compliance >= 60 ? '#FF9500' : '#FF3B30' }}>{report.sla.resolution_compliance}%</p>
                      <p className="text-[9px] text-[#9CA3AF]">Resolution SLA</p>
                      <p className="text-[8px] text-[#9CA3AF] mt-0.5">{report.sla.resolution_breached} breached</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-[#F3F4F6]" data-testid="sla-avg-response">
                      <p className="text-2xl font-bold text-[#007AFF]">{report.summary.avg_response_hours}h</p>
                      <p className="text-[9px] text-[#9CA3AF]">Avg First Response</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-[#F3F4F6]" data-testid="sla-avg-resolution">
                      <p className="text-2xl font-bold text-[#AF52DE]">{report.summary.avg_resolution_hours}h</p>
                      <p className="text-[9px] text-[#9CA3AF]">Avg Resolution</p>
                    </div>
                  </div>

                  {/* Breach by Priority */}
                  <div className="mt-4" data-testid="sla-breach-by-priority">
                    <p className="text-xs font-semibold text-[#0A0A0A] mb-2">Breaches by Priority</p>
                    <div className="grid grid-cols-4 gap-2">
                      {['critical', 'high', 'medium', 'low'].map(p => {
                        const count = report.sla.breach_by_priority?.[p] || 0;
                        const colors = { critical: '#FF3B30', high: '#FF9500', medium: '#007AFF', low: '#9CA3AF' };
                        return (
                          <div key={p} className="text-center p-2 rounded-lg border border-[#E5E7EB]" data-testid={`breach-${p}`}>
                            <p className="text-lg font-bold" style={{ color: count > 0 ? colors[p] : '#E5E7EB' }}>{count}</p>
                            <p className="text-[8px] text-[#9CA3AF] capitalize">{p}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* SLA Targets */}
                  <div className="mt-4 p-3 rounded-lg bg-[#002FA7]/[0.02] border border-[#002FA7]/10" data-testid="sla-targets">
                    <p className="text-xs font-semibold text-[#002FA7] mb-2">Current SLA Targets</p>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      {Object.entries(report.sla.targets || {}).map(([p, t]) => (
                        <div key={p} className="text-[9px]">
                          <p className="font-semibold capitalize text-[#0A0A0A]">{p}</p>
                          <p className="text-[#9CA3AF]">Resp: {t.response_hours}h</p>
                          <p className="text-[#9CA3AF]">Res: {t.resolution_hours}h</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Knowledge Gaps */}
              <Card className="border-[#E5E7EB]" data-testid="knowledge-gaps-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF3B30]" />Top Knowledge Gaps</CardTitle></CardHeader>
                <CardContent>
                  {gaps.length === 0 ? <p className="text-xs text-[#34C759] text-center py-4">No knowledge gaps detected</p> :
                    <div className="space-y-2">
                      {gaps.slice(0, 5).map((g, i) => (
                        <div key={i} className="p-2.5 rounded-lg border border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]" data-testid={`gap-${i}`}>
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="text-xs font-semibold text-[#0A0A0A] capitalize">{g.category}</h4>
                            <Badge className="text-[8px]" style={{ backgroundColor: g.gap_severity === 'high' ? '#FF3B3015' : '#FF950015', color: g.gap_severity === 'high' ? '#FF3B30' : '#FF9500' }}>{g.gap_severity}</Badge>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div><p className="text-sm font-bold text-[#FF3B30]">{g.ticket_count}</p><p className="text-[8px] text-[#9CA3AF]">Tickets</p></div>
                            <div><p className="text-sm font-bold text-[#34C759]">{g.self_service_count}</p><p className="text-[8px] text-[#9CA3AF]">Self-service</p></div>
                            <div><p className="text-sm font-bold text-[#FF9500]">{g.match_rate}%</p><p className="text-[8px] text-[#9CA3AF]">Match rate</p></div>
                            <div><p className="text-sm font-bold text-[#007AFF]">{g.article_count}</p><p className="text-[8px] text-[#9CA3AF]">Articles</p></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                </CardContent>
              </Card>
            </>
          ) : <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>}
        </TabsContent>
      </Tabs>

      {/* ==================== CREATE TICKET DIALOG ==================== */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-ticket-dialog">
          <DialogHeader><DialogTitle>{suggestions.length > 0 ? 'Articles That May Help' : 'Submit Support Ticket'}</DialogTitle></DialogHeader>
          {suggestions.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs text-[#4B5563] bg-[#34C759]/5 p-3 rounded-lg border border-[#34C759]/20">We found {suggestions.length} article(s) that might solve your issue. If one helps, you can close the ticket without waiting for an agent.</p>
              {suggestions.map((art, i) => (
                <Card key={i} className="border-[#E5E7EB]" data-testid={`suggestion-article-${i}`}>
                  <CardContent className="p-3">
                    <h4 className="text-sm font-medium text-[#0A0A0A] mb-1">{art.title}</h4>
                    <p className="text-xs text-[#4B5563] line-clamp-2 mb-2">{art.content?.substring(0, 150)}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[8px]">Score: {art._match_score}</Badge>
                      <Button size="sm" className="h-7 text-xs bg-[#34C759] hover:bg-[#2DB84E]" onClick={() => handleSelfResolve(art.article_id)} data-testid={`self-resolve-${i}`}>
                        <CheckCircle className="w-3 h-3 mr-1" />This Solved It
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button variant="outline" className="w-full text-xs" onClick={handleDismissSuggestions} data-testid="dismiss-suggestions-btn">
                None of these help — Submit to agent
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label>Title *</Label><Input value={ticketForm.title} onChange={e => setTicketForm(p => ({ ...p, title: e.target.value }))} placeholder="Brief description of your issue" data-testid="ticket-title-input" /></div>
              <div><Label>Description</Label><Textarea rows={3} value={ticketForm.description} onChange={e => setTicketForm(p => ({ ...p, description: e.target.value }))} placeholder="Provide details about the issue..." data-testid="ticket-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label><Select value={ticketForm.category} onValueChange={v => setTicketForm(p => ({ ...p, category: v }))}><SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="technical">Technical</SelectItem><SelectItem value="access">Access/Permissions</SelectItem><SelectItem value="feature">Feature Request</SelectItem><SelectItem value="bug">Bug Report</SelectItem></SelectContent></Select></div>
                <div><Label>Priority</Label><Select value={ticketForm.priority} onValueChange={v => setTicketForm(p => ({ ...p, priority: v }))}><SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} disabled={creating} data-testid="submit-ticket-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Submit Ticket
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== VIEW TICKET DIALOG ==================== */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl" data-testid="view-ticket-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            {selectedTicket && <><Hash className="w-4 h-4 text-[#002FA7]" /><span className="font-mono text-[#002FA7]">{fmtTktNum(selectedTicket.ticket_number)}</span><span className="text-[#9CA3AF] font-normal text-sm">Ticket Details</span></>}
          </DialogTitle></DialogHeader>
          {selectedTicket && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              {/* Status + SLA Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge style={{ backgroundColor: getStatus(selectedTicket.status).color + '15', color: getStatus(selectedTicket.status).color }}>{getStatus(selectedTicket.status).label}</Badge>
                <Badge variant="outline" className="text-[9px]">{selectedTicket.category}</Badge>
                <Badge variant="outline" className="text-[9px] capitalize">{selectedTicket.priority}</Badge>
                {selectedTicket.escalated && <Badge className="text-[8px] bg-[#FF3B30]/10 text-[#FF3B30]">Escalated from {selectedTicket.original_priority}</Badge>}
                {selectedTicket.resolution_type && <Badge className="text-[8px] bg-[#34C759]/10 text-[#34C759]">{selectedTicket.resolution_type.replace('_', ' ')}</Badge>}
              </div>

              {/* SLA Status Bar */}
              <div className="grid grid-cols-2 gap-2" data-testid="ticket-sla-status">
                <div className={`p-2 rounded-lg border ${selectedTicket.sla_response_status === 'breached' ? 'border-[#FF3B30]/30 bg-[#FF3B30]/[0.03]' : selectedTicket.sla_response_status === 'at_risk' ? 'border-[#FF9500]/30 bg-[#FF9500]/[0.03]' : 'border-[#34C759]/30 bg-[#34C759]/[0.03]'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-[#9CA3AF]">Response SLA ({selectedTicket.sla_response_target_hours}h)</span>
                    <SlaBadge status={selectedTicket.sla_response_status} />
                  </div>
                </div>
                <div className={`p-2 rounded-lg border ${selectedTicket.sla_resolution_status === 'breached' ? 'border-[#FF3B30]/30 bg-[#FF3B30]/[0.03]' : selectedTicket.sla_resolution_status === 'at_risk' ? 'border-[#FF9500]/30 bg-[#FF9500]/[0.03]' : 'border-[#34C759]/30 bg-[#34C759]/[0.03]'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-[#9CA3AF]">Resolution SLA ({selectedTicket.sla_resolution_target_hours}h)</span>
                    <SlaBadge status={selectedTicket.sla_resolution_status} />
                  </div>
                </div>
              </div>

              <h3 className="text-base font-semibold">{selectedTicket.title}</h3>
              <p className="text-xs text-[#4B5563]">{selectedTicket.description}</p>

              {/* People & Timestamps Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]" data-testid="ticket-details-grid">
                <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Submitted by</span><p className="font-medium">{selectedTicket.submitted_by_name}</p></div>
                <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Created</span><p>{format(new Date(selectedTicket.created_at), 'MMM d, yyyy h:mm a')}</p></div>
                <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Assigned to</span><p className={selectedTicket.assigned_to_name ? 'text-[#007AFF] font-medium' : 'text-[#9CA3AF]'}>{selectedTicket.assigned_to_name || 'Unassigned'}</p></div>
                <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Held by (In Progress)</span><p className={selectedTicket.held_by_name ? 'text-[#AF52DE] font-medium' : 'text-[#9CA3AF]'}>{selectedTicket.held_by_name || '—'}</p></div>
                <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Resolved by</span><p className={selectedTicket.resolved_by_name ? 'text-[#34C759] font-medium' : 'text-[#9CA3AF]'}>{selectedTicket.resolved_by_name || '—'}</p></div>
                <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Closed by</span><p className={selectedTicket.closed_by_name ? 'font-medium' : 'text-[#9CA3AF]'}>{selectedTicket.closed_by_name || '—'}</p></div>
                {selectedTicket.resolved_at && <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Resolved at</span><p>{format(new Date(selectedTicket.resolved_at), 'MMM d, yyyy h:mm a')}</p></div>}
                {selectedTicket.closed_at && <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">Closed at</span><p>{format(new Date(selectedTicket.closed_at), 'MMM d, yyyy h:mm a')}</p></div>}
                {selectedTicket.first_response_at && <div className="text-xs"><span className="text-[#9CA3AF] block text-[9px]">First response</span><p>{format(new Date(selectedTicket.first_response_at), 'MMM d, yyyy h:mm a')}</p></div>}
              </div>

              {/* Escalation notice */}
              {selectedTicket.escalated && (
                <div className="p-2.5 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20" data-testid="escalation-notice">
                  <p className="text-[9px] font-semibold text-[#FF3B30] mb-0.5">Escalated</p>
                  <p className="text-xs text-[#4B5563]">{selectedTicket.escalation_reason}</p>
                  <p className="text-[9px] text-[#9CA3AF] mt-0.5">Original priority: {selectedTicket.original_priority} | Escalated at: {selectedTicket.escalated_at ? format(new Date(selectedTicket.escalated_at), 'MMM d h:mm a') : '—'}</p>
                </div>
              )}

              {/* Resolution Notes */}
              {selectedTicket.resolution_notes && (
                <div className="p-3 rounded-lg bg-[#34C759]/5 border border-[#34C759]/20">
                  <p className="text-[9px] font-semibold text-[#34C759] mb-0.5">Resolution Notes</p>
                  <p className="text-xs text-[#4B5563]">{selectedTicket.resolution_notes}</p>
                </div>
              )}

              {/* Linked Articles */}
              {selectedTicket.linked_articles?.length > 0 && (
                <div><h4 className="text-xs font-semibold mb-1 flex items-center gap-1"><Link2 className="w-3 h-3 text-[#007AFF]" />Linked Articles</h4>
                  {selectedTicket.linked_articles.map((aid, i) => <Badge key={i} variant="outline" className="text-[9px] mr-1">{aid}</Badge>)}
                </div>
              )}

              {/* Similar Tickets */}
              {selectedTicket._similar_tickets?.length > 0 && (
                <div className="p-3 rounded-lg bg-[#007AFF]/5 border border-[#007AFF]/20">
                  <h4 className="text-xs font-semibold text-[#007AFF] mb-1">Similar Resolved Tickets</h4>
                  {selectedTicket._similar_tickets.map((st, i) => (
                    <div key={i} className="text-[10px] text-[#4B5563] py-0.5">{st.title} — <span className="text-[#9CA3AF]">{st.resolution_type}</span></div>
                  ))}
                </div>
              )}

              {/* Suggested Articles */}
              {selectedTicket._suggested_articles?.length > 0 && (
                <div className="p-3 rounded-lg bg-[#FF9500]/5 border border-[#FF9500]/20">
                  <h4 className="text-xs font-semibold text-[#FF9500] mb-1">Suggested KB Articles</h4>
                  {selectedTicket._suggested_articles.map((a, i) => (
                    <div key={i} className="text-[10px] text-[#4B5563] py-0.5">{a.title} — score: {a._match_score}</div>
                  ))}
                </div>
              )}

              {/* Agent Actions */}
              {selectedTicket.status !== 'closed' && (
                <div className="flex gap-2 pt-2 border-t border-[#E5E7EB] flex-wrap">
                  {!selectedTicket.assigned_to_name && (
                    <Button size="sm" variant="outline" className="text-xs text-[#007AFF] border-[#007AFF]/30" onClick={handleAssignToMe} data-testid="assign-to-me-btn">
                      <UserCheck className="w-3 h-3 mr-1" />Assign to Me
                    </Button>
                  )}
                  {selectedTicket.status !== 'in_progress' && selectedTicket.status !== 'resolved' && (
                    <Button size="sm" variant="outline" className="text-xs text-[#AF52DE] border-[#AF52DE]/30" onClick={handleHoldTicket} data-testid="hold-ticket-btn">
                      <Play className="w-3 h-3 mr-1" />Work on This
                    </Button>
                  )}
                  {selectedTicket.status !== 'resolved' && (
                    <Button size="sm" className="bg-[#34C759] hover:bg-[#2DB84E] text-xs" onClick={() => { setResolveForm({ resolution_notes: '', linked_articles: selectedTicket.linked_articles || [] }); setIsResolveOpen(true); }} data-testid="resolve-ticket-btn">
                      <CheckCircle className="w-3 h-3 mr-1" />Resolve
                    </Button>
                  )}
                  {selectedTicket.status === 'resolved' && (
                    <Button size="sm" variant="outline" className="text-xs" onClick={handleCloseTicket} data-testid="close-ticket-btn">
                      <XCircle className="w-3 h-3 mr-1" />Close Ticket
                    </Button>
                  )}
                  {!selectedTicket.knowledge_draft_created && selectedTicket.resolution_notes && (
                    <Button size="sm" variant="outline" className="text-xs text-[#5856D6] border-[#5856D6]/30" onClick={() => handleDraftArticle(selectedTicket.ticket_id)} data-testid="draft-article-btn">
                      <ArrowUpRight className="w-3 h-3 mr-1" />Draft KB Article
                    </Button>
                  )}
                </div>
              )}

              {/* Post-Resolution: Draft Article Prompt */}
              {(selectedTicket.status === 'resolved' || selectedTicket.status === 'closed') && !selectedTicket.knowledge_draft_created && (
                <div className="p-3 rounded-lg bg-[#5856D6]/5 border border-[#5856D6]/20">
                  <p className="text-xs text-[#5856D6] font-medium mb-1.5">Should this solution become a knowledge article?</p>
                  <Button size="sm" className="bg-[#5856D6] hover:bg-[#4845B0] text-xs" onClick={() => handleDraftArticle(selectedTicket.ticket_id)} data-testid="post-resolve-draft-btn">
                    <ArrowUpRight className="w-3 h-3 mr-1" />Yes, Create Draft Article
                  </Button>
                </div>
              )}
              {selectedTicket.knowledge_draft_created && (
                <div className="p-2 rounded-lg bg-[#34C759]/5 border border-[#34C759]/20">
                  <p className="text-xs text-[#34C759] flex items-center gap-1"><CheckCircle className="w-3 h-3" />KB draft article created: {selectedTicket.knowledge_article_id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== RESOLVE DIALOG ==================== */}
      <Dialog open={isResolveOpen} onOpenChange={setIsResolveOpen}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Resolve Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Resolution Notes *</Label><Textarea rows={3} value={resolveForm.resolution_notes} onChange={e => setResolveForm(p => ({ ...p, resolution_notes: e.target.value }))} placeholder="Describe how the issue was resolved..." data-testid="resolution-notes-input" /></div>
            <div><Label>Link KB Articles (article IDs, comma-separated)</Label><Input value={resolveForm.linked_articles.join(', ')} onChange={e => setResolveForm(p => ({ ...p, linked_articles: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="art_xxx, art_yyy" data-testid="link-articles-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResolveOpen(false)}>Cancel</Button>
            <Button className="bg-[#34C759] hover:bg-[#2DB84E]" onClick={handleAgentResolve} data-testid="confirm-resolve-btn">
              <CheckCircle className="w-4 h-4 mr-1" />Resolve Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpCenterPage;
